/**
 * AddRecipientDialog — modal pour ajouter un destinataire à une QR (Chunk 9.L.8).
 *
 * Backend : `POST /v1/quote-requests/:id/recipients` (9.L.7).
 *
 * UX :
 *   - Single insurer_code (1 à la fois) avec `<select>` natif alimenté par le
 *     registre dynamique tenant (assureurs actifs, 9.V.2c-2b), filtré des codes
 *     déjà présents sur la QR (anti-duplicate côté UI).
 *   - `recipient_email` optionnel (validation Zod email côté backend).
 *   - `language` optionnel default 'fr' (radio fr/en).
 *
 * Erreurs API (bilingues via `getApiErrorMessage`) :
 *   - 404 QUOTE_REQUESTS_NOT_FOUND               → bandeau red + close + parent refetch
 *   - 409 QUOTE_REQUESTS_RECIPIENTS_QR_LOCKED    → bandeau red (status non éditable)
 *   - 409 QUOTE_REQUEST_RECIPIENTS_DUPLICATE     → bandeau red (PG 23505 si race)
 *
 * Pattern parité `SendQuoteRequestDialog` / `CreateQuoteRequestDialog`.
 */

import { useEffect, useId, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { InsurerCatalogBoundary } from "../../../components/InsurerCatalogBoundary";
import { ApiException } from "../../../lib/api";
import { getApiErrorMessage, normalizeLang } from "../../../lib/apiErrorMessage";
import { useInsurerCatalog } from "../../../lib/queries/insurers";
import { useAddQuoteRequestRecipient, useQuoteRequest } from "../../../lib/queries/quoteRequests";
import { toast } from "../../../lib/toast";

type Props = {
  readonly quoteRequestId: string;
  readonly onClose: () => void;
};

export function AddRecipientDialog({ quoteRequestId, onClose }: Props) {
  const { t, i18n } = useTranslation();
  const lang = normalizeLang(i18n.resolvedLanguage);
  const titleId = useId();
  const insurerFieldId = useId();
  const emailFieldId = useId();
  const langFrId = useId();
  const langEnId = useId();
  const cancelBtnRef = useRef<HTMLButtonElement | null>(null);

  const detail = useQuoteRequest(quoteRequestId);
  const add = useAddQuoteRequestRecipient();
  const catalog = useInsurerCatalog();
  const { reset: resetMutation } = add;

  const [insurerCode, setInsurerCode] = useState<string>("");
  const [emailValue, setEmailValue] = useState("");
  const [language, setLanguage] = useState<"fr" | "en">("fr");

  /**
   * Assureurs ACTIFS non encore présents sur la QR (anti-duplicate UI, 9.V.2c-2b
   * : source dynamique registre). Un assureur désactivé n'est jamais proposé.
   */
  const availableInsurers = useMemo(() => {
    const existing = detail.data?.recipients ?? [];
    const taken = new Set(existing.map((r) => r.insurer_code));
    return catalog.activeOptions.filter((o) => !taken.has(o.value));
  }, [detail.data?.recipients, catalog.activeOptions]);

  useEffect(() => {
    resetMutation();
    setInsurerCode("");
    setEmailValue("");
    setLanguage("fr");
    queueMicrotask(() => cancelBtnRef.current?.focus());
  }, [resetMutation]);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !add.isPending) {
        e.preventDefault();
        onClose();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onClose, add.isPending]);

  const onConfirm = () => {
    if (!insurerCode) return;
    const trimmedEmail = emailValue.trim();
    add.mutate(
      {
        quoteRequestId,
        input: {
          insurer_code: insurerCode,
          ...(trimmedEmail ? { recipient_email: trimmedEmail } : {}),
          language,
        },
      },
      {
        onSuccess: () => {
          toast.success(t("quote_requests.recipients.add_dialog.success"));
          onClose();
        },
      },
    );
  };

  const apiErrorMsg =
    add.error instanceof ApiException
      ? getApiErrorMessage(add.error, lang) || t("errors.generic")
      : add.isError
        ? t("errors.generic")
        : null;

  const noInsurersLeft = availableInsurers.length === 0;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
    >
      <div
        aria-hidden="true"
        className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm"
        onClick={() => {
          if (!add.isPending) onClose();
        }}
      />
      <div className="motion-safe:animate-fade-in relative z-10 w-full max-w-md rounded-xl bg-white shadow-xl ring-1 ring-slate-200">
        <header className="border-b border-slate-100 px-5 py-4">
          <h2 id={titleId} className="text-base font-semibold text-slate-900">
            {t("quote_requests.recipients.add_dialog.title")}
          </h2>
        </header>

        <div className="space-y-4 px-5 py-5">
          <InsurerCatalogBoundary catalog={catalog}>
            {() =>
              availableInsurers.length === 0 ? (
                <p className="text-sm text-amber-700">
                  {t("quote_requests.recipients.add_dialog.no_insurers_left")}
                </p>
              ) : (
                <>
                  <div>
                    <label
                      htmlFor={insurerFieldId}
                      className="mb-1.5 block text-xs font-medium text-slate-700"
                    >
                      {t("quote_requests.recipients.add_dialog.insurer_label")}{" "}
                      <span aria-hidden="true" className="text-red-600">
                        *
                      </span>
                    </label>
                    <select
                      id={insurerFieldId}
                      value={insurerCode}
                      onChange={(e) => setInsurerCode(e.target.value)}
                      disabled={add.isPending}
                      className="focus-visible:border-brand-500 focus-visible:ring-brand-500 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 disabled:opacity-50"
                    >
                      <option value="">
                        {t("quote_requests.recipients.add_dialog.insurer_placeholder")}
                      </option>
                      {availableInsurers.map((ins) => (
                        <option key={ins.value} value={ins.value}>
                          {ins.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label
                      htmlFor={emailFieldId}
                      className="mb-1.5 block text-xs font-medium text-slate-700"
                    >
                      {t("quote_requests.recipients.add_dialog.email_label")}
                    </label>
                    <input
                      id={emailFieldId}
                      type="email"
                      maxLength={254}
                      value={emailValue}
                      onChange={(e) => setEmailValue(e.target.value)}
                      disabled={add.isPending}
                      placeholder={t("quote_requests.recipients.add_dialog.email_placeholder")}
                      className="focus-visible:border-brand-500 focus-visible:ring-brand-500 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 disabled:opacity-50"
                    />
                    <p className="mt-1.5 text-xs text-slate-500">
                      {t("quote_requests.recipients.add_dialog.email_hint")}
                    </p>
                  </div>

                  <fieldset className="space-y-1.5">
                    <legend className="text-xs font-medium text-slate-700">
                      {t("quote_requests.recipients.add_dialog.language_label")}
                    </legend>
                    <div className="flex items-center gap-4 text-sm">
                      <label
                        htmlFor={langFrId}
                        className="inline-flex items-center gap-2 text-slate-700"
                      >
                        <input
                          id={langFrId}
                          type="radio"
                          name="recipient-language"
                          value="fr"
                          checked={language === "fr"}
                          onChange={() => setLanguage("fr")}
                          disabled={add.isPending}
                          className="text-brand-600 focus-visible:ring-brand-500"
                        />
                        {t("quote_requests.recipients.add_dialog.language_fr")}
                      </label>
                      <label
                        htmlFor={langEnId}
                        className="inline-flex items-center gap-2 text-slate-700"
                      >
                        <input
                          id={langEnId}
                          type="radio"
                          name="recipient-language"
                          value="en"
                          checked={language === "en"}
                          onChange={() => setLanguage("en")}
                          disabled={add.isPending}
                          className="text-brand-600 focus-visible:ring-brand-500"
                        />
                        {t("quote_requests.recipients.add_dialog.language_en")}
                      </label>
                    </div>
                  </fieldset>
                </>
              )
            }
          </InsurerCatalogBoundary>

          {apiErrorMsg && (
            <div
              role="alert"
              className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
            >
              {apiErrorMsg}
            </div>
          )}
        </div>

        <footer className="flex items-center justify-end gap-2 border-t border-slate-100 px-5 py-4">
          <button
            ref={cancelBtnRef}
            type="button"
            onClick={onClose}
            disabled={add.isPending}
            className="focus-visible:ring-brand-500 touch-manipulation rounded-md px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:opacity-50"
          >
            {t("common.cancel")}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={add.isPending || !insurerCode || noInsurersLeft}
            aria-busy={add.isPending}
            className="bg-brand-600 hover:bg-brand-700 focus-visible:ring-brand-500 inline-flex touch-manipulation items-center gap-2 rounded-md px-4 py-2 text-sm font-semibold text-white shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:bg-slate-300"
          >
            {add.isPending && (
              <svg
                className="h-4 w-4 animate-spin motion-reduce:animate-none"
                viewBox="0 0 16 16"
                fill="none"
                aria-hidden="true"
              >
                <circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="2" opacity="0.25" />
                <path
                  d="M14 8a6 6 0 0 1-6 6"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
            )}
            {add.isPending
              ? t("quote_requests.recipients.add_dialog.submitting")
              : t("quote_requests.recipients.add_dialog.submit")}
          </button>
        </footer>
      </div>
    </div>
  );
}
